import { apiFetch } from '@crowdex/host-kit/renderer/http';
import type {
  NativeAgent,
  NativeGitActionResult,
  NativeGitGenerationContext,
  NativeGitReview,
  NativeGitSnapshot,
  NativeGitStatus,
  NativeGitWorktree,
  NativeWorkspaceAction,
  NativeWorkspaceEnvironment,
  NativeWorkspaceOpener,
  WorkspaceEntry,
} from '@crowdex/workspace-protocol';
import type { PullRequestCockpit } from '@crowdex/workspace-protocol/pull-request-cockpit';
import { type DesktopPlatform, electronPlatform, isElectronDesktop } from './electron-bridge';
import { isLocalMachine } from './local-runtime';
import { nativeInvoke as routeNativeInvoke } from './native-desktop-transport';

// The native client exposes the workspace protocol types used by its operations.
export type {
  NativeAgent,
  NativeGitActionResult,
  NativeGitGenerationContext,
  NativeGitReview,
  NativeGitSnapshot,
  NativeGitStatus,
  NativeGitWorktree,
  NativeWorkspaceAction,
  NativeWorkspaceEnvironment,
  NativeWorkspaceOpener,
  WorkspaceEntry,
};

/**
 * Plugin SDK contract (`@crowdex/plugin-sdk` renderer/native): true when
 * the app runs on the user's machine, so native workspace operations are
 * available. The app itself uses `isLocalMachine()`.
 */
export function isNativeDesktop(): boolean {
  return isLocalMachine();
}

/** The Electron shell's platform (window chrome, openers); `null` in a plain browser. */
export function nativeDesktopPlatform(): DesktopPlatform | null {
  return isElectronDesktop() ? electronPlatform() : null;
}

export type NativeManagedWorkspace = {
  rootPath: string;
  metadata: {
    schemaVersion: 'crowdex-managed-workspace/v1';
    id: string;
    kind: 'default' | 'conversation';
    retention: 'durable' | 'temporary';
    conversationId?: string;
    createdAt: string;
    lastUsedAt: string;
  };
};

/** Creates or reopens a Crowdex-owned workspace outside any selected repository. */
export function ensureNativeManagedWorkspace(
  input: { kind: 'default' } | { kind: 'conversation'; conversationId: string },
): Promise<NativeManagedWorkspace> {
  if (!isLocalMachine())
    return Promise.reject(
      new Error('Managed workspaces are only available when Crowdex runs on this machine.'),
    );
  return apiFetch<NativeManagedWorkspace>('/api/local/managed-workspaces', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(input),
  });
}

/** An agent's turn ended: `completed` when it finished, not when stopped, failed or cut off. */
export function endNativeBrowserTurn(taskId: string, completed = false): Promise<void> {
  return nativeInvoke<void>('browser_agent_turn_ended', { taskId, completed });
}

export type NativeBrowserUseActivity = {
  active: boolean;
  id: string | null;
  webviewLabel: string;
};

export function readNativeBrowserUseActivity(): Promise<NativeBrowserUseActivity[]> {
  return nativeInvoke<NativeBrowserUseActivity[]>('browser_agent_activity_snapshot');
}

export async function nativeInvoke<TResult>(
  command: string,
  args: Record<string, unknown> = {},
): Promise<TResult> {
  if (!isLocalMachine()) {
    throw new Error(`Native command "${command}" needs Crowdex running on this machine.`);
  }
  // Shared workspace/terminal operations always use the local CLI frame
  // protocol; only native OS/browser/plugin commands remain on Electron IPC.
  return routeNativeInvoke<TResult>(command, args);
}

export function discoverNativeAgents(): Promise<NativeAgent[]> {
  return nativeInvoke<NativeAgent[]>('acp_discover_agents');
}

/** What a commit would take: of `paths` (a conversation's files) only, or of the whole folder. */
export function readNativeGitGenerationContext(
  workspaceRoot: string,
  paths?: readonly string[],
): Promise<NativeGitGenerationContext> {
  return nativeInvoke<NativeGitGenerationContext>('workspace_git_generation_context', {
    workspaceRoot,
    ...(paths === undefined ? {} : { paths }),
  });
}

export function readNativeGitStatus(workspaceRoot: string): Promise<NativeGitStatus> {
  return nativeInvoke<NativeGitStatus>('workspace_git_status', { workspaceRoot });
}

export function readNativePullRequestCockpit(workspaceRoot: string): Promise<PullRequestCockpit> {
  return nativeInvoke<PullRequestCockpit>('workspace_pull_request_cockpit', {
    workspaceRoot,
  });
}

/** Clones a forge repository by its HTTPS clone URL into a folder of this machine. */
export async function cloneNativeSourceControlRepository(input: {
  destinationRoot: string;
  cloneUrl: string;
}): Promise<string> {
  return nativeInvoke<string>('workspace_git_clone', input);
}

/** Commits `paths` (a conversation's files) only, or, without, everything the folder changed. */
export function commitNativeGitChanges(input: {
  workspaceRoot: string;
  message: string;
  push: boolean;
  paths?: readonly string[];
}): Promise<NativeGitActionResult> {
  return nativeInvoke<NativeGitActionResult>('workspace_git_commit', input);
}

export function pushNativeGitBranch(workspaceRoot: string): Promise<NativeGitActionResult> {
  return nativeInvoke<NativeGitActionResult>('workspace_git_push', { workspaceRoot });
}

export function createNativeGitPullRequest(input: {
  workspaceRoot: string;
  title: string;
  body: string;
  draft: boolean;
}): Promise<NativeGitActionResult> {
  return nativeInvoke<NativeGitActionResult>('workspace_git_create_pull_request', input);
}

export function mergeNativeGitPullRequest(input: {
  workspaceRoot: string;
  method: 'merge' | 'squash';
  expectedHeadSha: string;
}): Promise<void> {
  return nativeInvoke<void>('workspace_git_merge_pull_request', input);
}

export function mutateNativeGitPullRequest(input: {
  workspaceRoot: string;
  action:
    | 'close'
    | 'comment'
    | 'disable_auto_merge'
    | 'draft'
    | 'edit'
    | 'enable_auto_merge'
    | 'merge'
    | 'ready'
    | 'react_comment'
    | 'reply_thread'
    | 'reopen'
    | 'request_reviewers'
    | 'resolve_thread'
    | 'submit_review'
    | 'update_branch';
  expectedHeadSha: string;
  title?: string;
  body?: string;
  decision?: 'approve' | 'comment' | 'request_changes';
  reviewers?: string[];
  method?: 'merge' | 'squash';
  threadId?: string;
  commentId?: string;
  reaction?:
    | 'THUMBS_UP'
    | 'THUMBS_DOWN'
    | 'LAUGH'
    | 'HOORAY'
    | 'CONFUSED'
    | 'HEART'
    | 'ROCKET'
    | 'EYES';
}): Promise<void> {
  return nativeInvoke<void>('workspace_git_mutate_pull_request', input);
}

/** A branch to check out: a local branch, or the remote's version of one. */
export type BranchTarget = { branch: string; remote: boolean };

/** What a switch did; see `switchWorkspaceBranch` in `@crowdex/workspace-runtime`. */
export type NativeBranchSwitchResult =
  | { status: 'switched' }
  | { status: 'diverged'; ahead: number; behind: number };

export type DivergedBranchChoice = 'keep-local' | 'use-remote';

export function switchNativeGitBranch(
  workspaceRoot: string,
  target: BranchTarget,
  stash = false,
  diverged: DivergedBranchChoice | null = null,
): Promise<NativeBranchSwitchResult> {
  return nativeInvoke<NativeBranchSwitchResult>('workspace_git_switch_branch', {
    workspaceRoot,
    branch: target.branch,
    stash,
    remote: target.remote,
    diverged,
  });
}

/** Refreshes the remote branches of the workspace (`git fetch --prune origin`). */
export function fetchNativeGitRemote(workspaceRoot: string): Promise<void> {
  return nativeInvoke<void>('workspace_git_fetch', { workspaceRoot });
}

export function createNativeGitBranch(workspaceRoot: string, branch: string): Promise<void> {
  return nativeInvoke<void>('workspace_git_create_branch', { workspaceRoot, branch });
}

/**
 * Makes a conversation's managed worktree of `workspaceRoot`, or reuses it. `worktreePath`, the
 * one it has already (the server's record), is reused, restored or made again in place; without
 * one, a new worktree goes under `worktreeRoot` (the machine's setting).
 */
export function createNativeManagedGitWorktree(input: {
  workspaceRoot: string;
  ownerThreadId: string;
  environment?: NativeWorkspaceEnvironment | null;
  startingBranch?: BranchTarget | null;
  worktreeRoot?: string | null;
  worktreePath?: string | null;
}): Promise<NativeGitWorktree> {
  return nativeInvoke<NativeGitWorktree>('workspace_git_create_managed_worktree', {
    workspaceRoot: input.workspaceRoot,
    ownerThreadId: input.ownerThreadId,
    environment: input.environment ?? null,
    startingBranch: input.startingBranch?.branch ?? null,
    startingRemote: input.startingBranch?.remote === true,
    worktreeRoot: input.worktreeRoot ?? null,
    worktreePath: input.worktreePath ?? null,
  });
}

export function handoffNativeGitWorktree(
  worktreePath: string,
  destination: 'local' | 'worktree',
): Promise<{ path: string }> {
  return nativeInvoke<{ path: string }>('workspace_git_handoff_worktree', {
    worktreePath,
    destination,
  });
}

export function restoreNativeGitWorktree(worktreePath: string): Promise<{ path: string } | null> {
  return nativeInvoke<{ path: string } | null>('workspace_git_restore_worktree', {
    worktreePath,
  });
}

export function cleanupNativeGitWorktrees(
  workspaceRoot: string,
  protectedOwnerIds: readonly string[],
  keepCount: number,
  worktreeRoot: string | null,
): Promise<string[]> {
  return nativeInvoke<string[]>('workspace_git_cleanup_worktrees', {
    workspaceRoot,
    protectedOwnerIds: [...protectedOwnerIds],
    keepCount,
    worktreeRoot,
  });
}

export function readNativeWorkspaceEnvironment(
  workspaceRoot: string,
): Promise<NativeWorkspaceEnvironment | null> {
  return nativeInvoke<NativeWorkspaceEnvironment | null>('workspace_environment_get', {
    workspaceRoot,
  });
}

export function writeNativeWorkspaceEnvironment(
  workspaceRoot: string,
  environment: NativeWorkspaceEnvironment | null,
): Promise<void> {
  return nativeInvoke<void>('workspace_environment_set', { workspaceRoot, environment });
}

export function listNativeWorkspaceOpeners(): Promise<NativeWorkspaceOpener[]> {
  if (!isLocalMachine()) return Promise.resolve([]);
  return apiFetch<NativeWorkspaceOpener[]>('/api/local/openers');
}

export async function openNativeWorkspaceIn(
  workspaceRoot: string,
  openerId: string,
): Promise<void> {
  await apiFetch('/api/local/openers', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ workspaceRoot, openerId }),
  });
}

export function killNativeAgentTerminal(terminalId: string): Promise<void> {
  return nativeInvoke<void>('agent_terminal_kill', { terminalId });
}

export function readNativeAgentBackgroundOutput(path: string): Promise<string> {
  return nativeInvoke<string>('agent_terminal_read_background_output', { path });
}

export function killNativeAgentBackgroundTask(path: string): Promise<void> {
  return nativeInvoke<void>('agent_terminal_kill_background', { path });
}
